import { compact } from 'lodash';

import {
  AugmentedCreateClientProfileInput,
  AugmentedCreateHouseholdInput,
  HouseholdBillableKind,
} from '@/types/schema';

import {
  ClientSetupClientFormShape,
  ClientSetupFormShape,
} from './ClientSetup.type';

/**
 * Maps the client-setup form into the CreateHouseholdV2 mutation input.
 */
export function mapClientSetupFormToCreateHouseholdInput(
  values: ClientSetupFormShape
): AugmentedCreateHouseholdInput {
  const { clientOne, clientTwo, defaultDeathOrder, ...formValues } = values;

  // The order of profiles determines the default death order in the system.
  // The first profile in the array is treated as the "first to die" by default.
  // Only swap the order if secondary dies first AND clientTwo has the required data.
  const clientTwoHasRequiredData = Boolean(
    clientTwo.firstName && clientTwo.lastName
  );

  const orderedClients =
    defaultDeathOrder === 'secondary' && clientTwoHasRequiredData
      ? [clientTwo, clientOne]
      : [clientOne, clientTwo];

  return {
    create: {
      primaryRelationshipOwnerID: formValues.primaryRelationshipOwnerId,
      assignedTeamIDs: compact(formValues.teamIds),
      integrationClientIDs: compact(formValues.integrationClientIds),
      billableKind: formValues.isProspect
        ? HouseholdBillableKind.Prospect
        : null,
      subBrandID: formValues.subBrandId || null,
    },
    withClientProfiles: compact(
      orderedClients.map(
        (
          formProfile: ClientSetupClientFormShape
        ): AugmentedCreateClientProfileInput | null => {
          if (!formProfile.firstName || !formProfile.lastName) {
            return null;
          }

          return {
            create: {
              // householdID is overwritten server-side via context propagation
              // on nested creates under Household.AugmentedCreate.
              householdID: '',
              firstName: formProfile.firstName?.trim() || '',
              middleName: formProfile.middleName?.trim() || '',
              lastName: formProfile.lastName?.trim() || '',
              suffix: formProfile.suffix?.trim() || '',
              // Left undefined rather than '' when blank so the server stores
              // null and falls back to the legal name for display.
              preferredName: formProfile.preferredName?.trim() || undefined,
              isPrimary: !formProfile.isDeceased,
              isDeceased: formProfile.isDeceased,
              isGrantor: true,
            },
            withAddress: {
              create: {
                // householdID is overwritten server-side via context propagation
                // on nested creates under Household.AugmentedCreate.
                householdID: '',
                stateCode: formProfile.stateCode,
              },
            },
          };
        }
      )
    ),
  };
}
